import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import { Badge, Card, Pagination, QueryBoundary, Table, Td, Th } from '../components/ui';
import { dateTime, isoDaysAgo, titleCase } from '../lib/format';

const LIMIT = 100;
const RANGES = [1, 7, 30, 90];

export default function Signals() {
  const [days, setDays] = useState(7);
  const [symbol, setSymbol] = useState('');
  const [offset, setOffset] = useState(0);

  const fromUtc = isoDaysAgo(days);
  const toUtc = new Date().toISOString();

  const funnel = useQuery({
    queryKey: ['funnel', days, symbol],
    queryFn: () => api.funnel(fromUtc, toUtc, symbol || undefined),
  });

  const rejections = useQuery({
    queryKey: ['rejections', days, symbol, offset],
    queryFn: () =>
      api.rejections({
        from_utc: fromUtc,
        to_utc: toUtc,
        symbol: symbol || undefined,
        limit: LIMIT,
        offset,
      }),
  });

  const f = funnel.data;
  const maxStage = f ? Math.max(1, ...f.stages.map((s) => s.entering)) : 1;

  return (
    <>
      <PageHeader
        title="Signals"
        description="Why candidates did or did not become trades (ARCHITECTURE §12.2)."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => {
              setDays(r);
              setOffset(0);
            }}
            className={`rounded border px-2 py-1 text-xs ${
              days === r ? 'border-slate-500 text-slate-100' : 'border-edge text-slate-400'
            }`}
          >
            {r}d
          </button>
        ))}
        <input
          value={symbol}
          onChange={(e) => {
            setSymbol(e.target.value.toUpperCase());
            setOffset(0);
          }}
          placeholder="Symbol"
          className="rounded border border-edge bg-surface px-2 py-1 text-sm outline-none focus:border-slate-500"
        />
      </div>

      <QueryBoundary q={funnel}>
        {f && (
          <Card className="mb-6 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-medium text-slate-300">Funnel</h2>
              <Badge tone={f.reconciles ? 'good' : 'bad'}>
                {f.reconciles ? 'reconciles' : 'does not reconcile'}
              </Badge>
            </div>
            <div className="space-y-1.5">
              {f.stages.map((s) => (
                <div key={s.label} className="flex items-center gap-3 text-sm">
                  <span className="w-40 shrink-0 text-slate-400">{titleCase(s.label)}</span>
                  <div className="h-4 flex-1 overflow-hidden rounded bg-surface">
                    <div
                      className="h-full bg-sky-700/70"
                      style={{ width: `${(s.entering / maxStage) * 100}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right tabular-nums text-slate-300">
                    {s.entering}
                    {s.dropped > 0 && <span className="text-red-400"> −{s.dropped}</span>}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
              {(
                [
                  ['candidates', f.candidates],
                  ['rejected', f.rejected],
                  ['selected', f.selected],
                  ['risk approved', f.risk_approved],
                  ['executed', f.executed],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="rounded border border-edge bg-surface px-2 py-1.5">
                  <div className="text-slate-500">{k}</div>
                  <div className="tabular-nums text-slate-200">{v}</div>
                </div>
              ))}
            </div>
            {f.by_reason.length > 0 && (
              <div className="mt-4">
                <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">By reason</div>
                <div className="flex flex-wrap gap-1.5">
                  {f.by_reason.map((r) => (
                    <Badge key={r.reason_code}>
                      {titleCase(r.reason_code)}: {r.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </QueryBoundary>

      <h2 className="mb-2 text-sm font-medium text-slate-300">Rejection log</h2>
      <QueryBoundary q={rejections} isEmpty={rejections.data?.items.length === 0}>
        {rejections.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Bar</Th>
                  <Th>Strategy</Th>
                  <Th>Symbol / TF</Th>
                  <Th>Reason</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {rejections.data.items.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-800/30">
                    <Td className="whitespace-nowrap text-xs text-slate-400">
                      {dateTime(r.bar_open_time_utc)}
                    </Td>
                    <Td>{r.strategy_id}</Td>
                    <Td>
                      {r.symbol} · {r.timeframe}
                    </Td>
                    <Td>
                      <Badge tone="warn">{titleCase(r.reason_code)}</Badge>
                    </Td>
                    <Td className="text-slate-400">{r.detail}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={rejections.data.total}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </QueryBoundary>
    </>
  );
}
